import React from 'react';

import { createBrowserRouter } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import BudgetsPage from '@/pages/Budgets';
import BudgetAddPage from '@/pages/Budgets/Add';
import BudgetDetailPage from '@/pages/Budgets/Detail';
import ForgotPasswordPage from '@/pages/ForgotPassword';
import LandingPage from '@/pages/Landing';
import LoginPage from '@/pages/Login';
import OnboardingLayout from '@/pages/Onboarding/OnboardingLayout';
import OverviewPage from '@/pages/Onboarding/Overview';
import OnboardingProfilePage from '@/pages/Onboarding/Profile';
import SubscriptionPage from '@/pages/Onboarding/Subscription';
import PrivacyPolicyPage from '@/pages/PrivacyPolicy';
import ResetPasswordPage from '@/pages/ResetPassword';
import SignUpPage from '@/pages/SignUp';
import TermsPage from '@/pages/Terms';
import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';
import NotFoundPage from '@/pages/NotFound';
import ProfilePage from '@/pages/Profile';

import { Pages } from './types';
import DashboardPage from '@/pages/Dashboard';
import CommitmentPage from '@/pages/Commitment';
import InvoicePage from '@/pages/Invoice';
import ProfileLayout from '@/pages/Profile/ProfileLayout';
import EditProfilePage from '@/pages/Profile/Edit/EditProfilePage';
import ProfileSubscriptionPage from '@/pages/Profile/Subscription/SubscriptionPage';
import BillingHistoryPage from '@/pages/Profile/BillingHistory/BillingHistoryPage';
import TerminologyPage from '@/pages/Profile/Terminology/TerminologyPage';
import PaymentMethodPage from '@/pages/Profile/PaymentMethod/PaymentMethodPage';
import BankAccountPage from '@/pages/BankAccount';
import BankAccountAddPage from '@/pages/BankAccount/Add';
import EditPage from '@/pages/Budgets/Edit/EditBudgetPage';
import BankAccountOutlet from '@/pages/BankAccount/BankAccountOutlet';
import BankAccountDetailPage from '@/pages/BankAccount/Detail/BankAccountDetailPage';
import BankAccountEditPage from '@/pages/BankAccount/Edit/BankAccountEditPage';
import AccountingCenterOutlet from '@/pages/AccountingCenter';
import AccountingCentersPage from '@/pages/AccountingCenter/AccountingCentersPage';
import ACAddPage from '@/pages/AccountingCenter/Add/ACAddPage';
import ACEditPage from '@/pages/AccountingCenter/Edit/ACEditPage';
import ACDetailPage from '@/pages/AccountingCenter/Detail/ACDetailPage';
import ACAdjustPage from '@/pages/AccountingCenter/Adjust/ACAdjust';
import ExpenseCategoryOutlet from '@/pages/ExpenseCategory';
import ExpenseCategoryPage from '@/pages/ExpenseCategory/ExpenseCategoryPage';

export const PAGE: Pages = {
  LANDING: {
    path: '/',
    navTitle: 'Landing'
  },
  LOGIN: {
    path: '/login',
    navTitle: 'Login'
  },
  SIGNUP: {
    path: '/signup',
    navTitle: 'SignUp'
  },
  ONBOARDING: {
    path: '/onboarding',
    navTitle: 'Onboarding',
    children: {
      PROFILE: 'profile',
      OVERVIEW: 'overview',
      SUBSCRIPTION: 'subscription'
    }
  },
  TERMS: {
    path: '/terms',
    navTitle: 'Terms'
  },
  PRIVACY_POLICY: {
    path: '/privacy-policy',
    navTitle: 'PrivacyPolicy'
  },
  FORGOT_PASSWORD: {
    path: '/forgot-password',
    navTitle: 'ForgotPassword'
  },
  RESET_PASSWORD: {
    path: '/reset-password',
    navTitle: 'ResetPassword'
  },
  BUDGETS: {
    path: '/budgets',
    navTitle: 'Budgets',
    children: {
      ADD: 'add',
      DETAIL: ':budgetId',
      EDIT: 'edit/:budgetId'
    }
  },
  BANK_ACCOUNTS: {
    path: '/budgets/:budgetId/bank-accounts',
    navTitle: 'Bank Accounts',
    children: {
      ADD: 'add',
      DETAIL: ':bankAccountId',
      EDIT: ':bankAccountId/edit'
    }
  },
  DASHBOARD: {
    path: '/dashboard',
    navTitle: 'Dashboard'
  },
  PROFILE: {
    path: '/profile',
    navTitle: 'Profile',
    children: {
      EDIT: 'edit',
      SUBSCRIPTION: 'subscription',
      BILLING_HISTORY: 'billing-history',
      TERMINOLOGY: 'terminology',
      PAYMENT_METHOD: 'payment-method'
    }
  },
  COMMITMENT: {
    path: '/commitment',
    navTitle: 'Commitment'
  },
  INVOICE: {
    path: '/invoice',
    navTitle: 'Invoice'
  },
  ACCOUNTING_CENTER: {
    path: '/budgets/:budgetId/accounting-centers/:acType',
    navTitle: 'Accounting Centers',
    children: {
      ADD: 'add',
      EDIT: 'edit/:acId',
      DETAIL: ':acId',
      ADJUST: 'adjust'
    }
  },
  EXPENSE_CATEGORY: {
    path: '/budgets/:budgetId/expense-categories',
    navTitle: 'Expense Categories',
    children: {
      ADD: 'add',
      EDIT: 'edit/:categoryId',
      DETAIL: ':categoryId',
      ADJUST: 'adjust'
    }
  }
} as const;

export const routes = createBrowserRouter([
  {
    path: PAGE.LANDING.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(LandingPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.LOGIN.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(LoginPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.SIGNUP.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(SignUpPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.ONBOARDING.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(OnboardingLayout) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(Navigate, {
          to: `${PAGE.ONBOARDING.path}/${PAGE.ONBOARDING.children.PROFILE}`,
          replace: true
        }) as React.ReactElement
      },
      {
        path: PAGE.ONBOARDING.children.PROFILE,
        element: React.createElement(OnboardingProfilePage) as React.ReactElement
      },
      {
        path: PAGE.ONBOARDING.children.OVERVIEW,
        element: React.createElement(OverviewPage) as React.ReactElement
      },
      {
        path: PAGE.ONBOARDING.children.SUBSCRIPTION,
        element: React.createElement(SubscriptionPage) as React.ReactElement
      }
    ]
  },
  // Public routes without auth check
  {
    path: PAGE.TERMS.path,
    element: React.createElement(TermsPage) as React.ReactElement
  },
  {
    path: PAGE.PRIVACY_POLICY.path,
    element: React.createElement(PrivacyPolicyPage) as React.ReactElement
  },
  {
    path: PAGE.FORGOT_PASSWORD.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(ForgotPasswordPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.RESET_PASSWORD.path,
    element: React.createElement(PublicRoute, {
      children: React.createElement(ResetPasswordPage) as React.ReactElement,
      redirectTo: '/dashboard'
    }) as React.ReactElement
  },
  {
    path: PAGE.BUDGETS.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(BudgetsPage) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        path: PAGE.BUDGETS.children.ADD,
        element: React.createElement(BudgetAddPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.DETAIL,
        element: React.createElement(BudgetDetailPage) as React.ReactElement
      },
      {
        path: PAGE.BUDGETS.children.EDIT,
        element: React.createElement(EditPage) as React.ReactElement
      }
    ]
  },
  // Bank accounts routes
  {
    path: PAGE.BANK_ACCOUNTS.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(BankAccountOutlet) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(BankAccountPage) as React.ReactElement
      },
      {
        path: PAGE.BANK_ACCOUNTS.children.DETAIL,
        element: React.createElement(BankAccountDetailPage) as React.ReactElement
      },
      {
        path: PAGE.BANK_ACCOUNTS.children.EDIT,
        element: React.createElement(BankAccountEditPage) as React.ReactElement
      },
      {
        path: PAGE.BANK_ACCOUNTS.children.ADD,
        element: React.createElement(BankAccountAddPage) as React.ReactElement
      }
    ]
  },
  {
    path: PAGE.DASHBOARD.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(DashboardPage) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement
  },
  {
    path: PAGE.PROFILE.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(ProfileLayout) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(ProfilePage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.EDIT,
        element: React.createElement(EditProfilePage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.SUBSCRIPTION,
        element: React.createElement(ProfileSubscriptionPage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.BILLING_HISTORY,
        element: React.createElement(BillingHistoryPage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.TERMINOLOGY,
        element: React.createElement(TerminologyPage) as React.ReactElement
      },
      {
        path: PAGE.PROFILE.children.PAYMENT_METHOD,
        element: React.createElement(PaymentMethodPage) as React.ReactElement
      }
    ]
  },
  {
    path: PAGE.COMMITMENT.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(CommitmentPage) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement
  },
  {
    path: PAGE.INVOICE.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(InvoicePage) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement
  },
  // Accounting center routes
  {
    path: PAGE.ACCOUNTING_CENTER.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(AccountingCenterOutlet) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(AccountingCentersPage) as React.ReactElement
      },
      {
        path: PAGE.ACCOUNTING_CENTER.children.ADD,
        element: React.createElement(ACAddPage) as React.ReactElement
      },
      {
        path: PAGE.ACCOUNTING_CENTER.children.EDIT,
        element: React.createElement(ACEditPage) as React.ReactElement
      },
      {
        path: PAGE.ACCOUNTING_CENTER.children.DETAIL,
        element: React.createElement(ACDetailPage) as React.ReactElement
      },
      {
        path: PAGE.ACCOUNTING_CENTER.children.ADJUST,
        element: React.createElement(ACAdjustPage) as React.ReactElement
      }
    ]
  },
  // Expense category routes
  {
    path: PAGE.EXPENSE_CATEGORY.path,
    element: React.createElement(PrivateRoute, {
      children: React.createElement(ExpenseCategoryOutlet) as React.ReactElement,
      redirectTo: '/login'
    }) as React.ReactElement,
    children: [
      {
        index: true,
        element: React.createElement(ExpenseCategoryPage) as React.ReactElement
      }
    ]
  },
  // 404 route
  {
    path: '*',
    element: React.createElement(NotFoundPage) as React.ReactElement
  }
]);

export default routes;
